import UmlCompositeShape from '../Types/UmlCompositeShape';
import UmlSeqParticipantShape from '../Types/UmlSeqParticipantShape';
import UmlSeqMessageShape from '../Types/UmlSeqMessageShape';
import UmlSeqActivationShape from '../Types/UmlSeqActivationShape';
import umlShapeSerializer from './UmlShapeSerializer';
import packageContainmentHandler from './PackageContainmentHandler';
import umlConnectorEngine from './UmlConnectorEngine';
import umlViewportEngine from './UmlViewportEngine';
import umlUndoEngine from './UmlUndoEngine/UmlUndoEngine';
import { findById, findMaxZIndex, findNestedShape, collectShapeIdsDeep } from './UmlCanvasEngineHelper';
import { cascadeSeqDiaMove, clampSeqMessageY, cascadeNestedSeqDiaShift } from './CsSeqDiaRelationHandler';

function isPointInBounds(px, py, bounds) {
    return px >= bounds.x && px <= bounds.x + bounds.w && py >= bounds.y && py <= bounds.y + bounds.h;
}

function translateChildren(compositeShape, dx, dy, allShapes) {
    compositeShape.children.forEach(function moveChild(child) {
        child.moveTo(child.x + dx, child.y + dy);
        if (child.caption) {
            child.caption.moveTo(child.caption.x + dx, child.caption.y + dy);
        }
        // A package-child participant dragged along with its package must cascade
        // to its lifeline/activations/messages/bottom-twin too (2026-07-19) — those
        // are top-level shapes, not package children, so they're invisible to the
        // recursive composite-child walk below and need this explicit call.
        cascadeSeqDiaMove(allShapes, child, dx, dy);
        if (child instanceof UmlCompositeShape) {
            translateChildren(child, dx, dy, allShapes);
        }
    });
}

/**
 * UML canvas engine — holds shapes for the workspace (connectors live in
 * UmlConnectorEngine, see that file). Pure pub/sub, no React knowledge here.
 */
class UmlCanvasEngine {
    constructor() {
        this.shapes = []; // top-level UmlShape instances (UmlPackageShape, UmlClassShape, UmlCommentShape)
        this.viewConfig = { hideInterfaces: false, derivedLabelOffsets: {}, seqParamsMode: 'hidden' }; // presentation-only, persisted alongside shapes/connectors
        this.nextZIndex = 1;
        this.listeners = [];
        // Identity of the currently loaded UmlSchema note — single source of truth so it
        // survives UmlSchemaConfigDlg being closed (that dialog's instance is fully destroyed
        // on close, see JxWindowManager.closeWindow, not just hidden). rk stays null for an
        // unsaved/new schema.
        this.loadedSchemaInfo = { rk: null, name: '', remarks: '' };
        // Snapshot of toSchema() as of the last load/save, JSON-stringified for cheap
        // comparison. null until the first loadFromSchema/markSaved call (nothing to
        // compare against yet, isDirty() reports false rather than guessing).
        this._savedSnapshot = null;

        // Cascade (2026-07-14): connectors are schema CONTENT (same tier as
        // shapes/viewConfig, all persisted via toSchema()), but they live in a
        // SEPARATE engine with its own notify() — straightenAllConnectors (Fix
        // Lines), addConnector/removeConnector, add/remove/reattach a point or
        // anchor all notify ONLY umlConnectorEngine's own listeners. Any
        // consumer that only subscribed to umlCanvasEngine (e.g. the Body tab
        // JSON preview) went stale after exactly those actions — reported as
        // "Fix Lines + Save doesn't stick", root cause was a missed notify, not
        // the save itself. Rather than auditing/patching every current AND
        // future consumer individually, fix it once here: re-fire THIS engine's
        // notify whenever umlConnectorEngine fires its own. Subscribing to
        // umlCanvasEngine alone is now reliably sufficient for "did the schema
        // content change" — no need to also remember umlConnectorEngine.
        // Deliberately NOT done for umlViewportEngine/umlSelectionEngine — those
        // are high-frequency, non-content state (pan/zoom/selection); cascading
        // them here would make every content consumer re-render/re-stringify on
        // every mouse move for no reason.
        umlConnectorEngine.subscribe(() => this.notify());
    }

    subscribe(callback) {
        this.listeners.push(callback);
        return function unsubscribe() {
            this.listeners = this.listeners.filter(function filterOutListener(listener) {
                return listener !== callback;
            });
        }.bind(this);
    }

    notify() {
        this.listeners.forEach(function callListener(listener) {
            listener();
        });
    }

    /**
     * Reconstructs shapes/connectors via umlShapeSerializer (input is always plain JSON).
     * @param {object} schemaData - raw schema { shapes, connectors, viewConfig }
     */
    loadFromSchema(schemaData) {
        const { shapes, connectors } = umlShapeSerializer.fromPlainSchema(schemaData);
        this.shapes = shapes;
        umlConnectorEngine.loadConnectors(connectors);
        this.viewConfig = { hideInterfaces: false, derivedLabelOffsets: {}, userHiddenItems: [], seqParamsMode: 'hidden', ...schemaData.viewConfig };
        this.nextZIndex = findMaxZIndex(shapes) + 1; // resume z-order above existing max
        // Restores the saved camera position (chat 2026-07-14: a large schema
        // shouldn't force the user to re-zoom/re-pan every time it's opened).
        // Undefined fields (older schemas without a saved viewport) fall back to
        // defaults inside restoreViewport itself.
        umlViewportEngine.restoreViewport(schemaData.viewport?.scale, schemaData.viewport?.panX, schemaData.viewport?.panY);
        this.markSaved();
        this.notify();
    }

    /**
     * Snapshots the current schema as the "saved" baseline for isDirty() — call
     * after loading (already matches the source) or after a successful save.
     */
    markSaved() {
        this._savedSnapshot = JSON.stringify(this.toSchema());
    }

    /**
     * True if the current schema differs from the last load/save snapshot.
     * False before any load/save has happened yet (nothing to compare against).
     * @returns {boolean}
     */
    isDirty() {
        if (this._savedSnapshot === null) return false;
        return JSON.stringify(this.toSchema()) !== this._savedSnapshot;
    }

    toSchema() {
        return {
            shapes: this.shapes,
            connectors: umlConnectorEngine.connectors,
            viewConfig: this.viewConfig,
        };
    }

    // Adds viewport (zoom/pan) on top of toSchema() — used ONLY when actually
    // persisting to the note (see UmlCanvasSaveAction.saveCurrentSchema), never
    // for isDirty()/markSaved() comparisons. Kept deliberately separate from
    // toSchema(): just panning/zooming to look around a large schema shouldn't
    // flag it as "unsaved changes" the way an actual shape/connector edit does
    // — only toSchema()'s output (unchanged) feeds the dirty-check.
    toSaveSchema() {
        return {
            ...this.toSchema(),
            viewport: { scale: umlViewportEngine.scale, panX: umlViewportEngine.panX, panY: umlViewportEngine.panY },
        };
    }

    // Identity of the loaded note (rk/name/remarks) — set on New/Load/Save by UmlSchemaConfigDlg.
    setLoadedSchemaInfo(info) {
        this.loadedSchemaInfo = { ...this.loadedSchemaInfo, ...info };
        this.notify();
    }

    // Presentation-only flag (Hide Interfaces toolbar toggle), never mutates shapes/connectors.
    setHideInterfaces(hideInterfaces) {
        this.viewConfig = { ...this.viewConfig, hideInterfaces };
        this.notify();
    }

    // CsSeqDia parameter-visibility toggle (hotkey P) — cycles hidden -> types ->
    // full (types+names) -> hidden. Same presentation-only pattern as
    // setHideInterfaces: never mutates the message shapes themselves (their
    // own .params stays as generated), purely controls what the widget renders.
    cycleSeqParamsMode() {
        const order = ['hidden', 'types', 'full'];
        const next = order[(order.indexOf(this.viewConfig.seqParamsMode) + 1) % order.length];
        this.viewConfig = { ...this.viewConfig, seqParamsMode: next };
        this.notify();
    }

    // Presentation-only drag offset for a derived "via <Interface>" label, keyed by connector id.
    setDerivedLabelOffset(connectorId, x, y) {
        this.viewConfig = {
            ...this.viewConfig,
            derivedLabelOffsets: { ...this.viewConfig.derivedLabelOffsets, [connectorId]: { x, y } },
        };
        this.notify();
    }

    // Presentation-only hide/show toggle shared by shapes AND connectors — id is
    // globally unique across both (UmlConnector.select() uses the same
    // umlSelectionEngine.select(this.id) as shapes), so one flat id-list works for
    // either. Never mutates shapes/connectors themselves — see
    // resolveUserHiddenItems.js for the derived paint-time filter that reads this.
    //
    // Hiding is ancestor/descendant-aware (chat 2026-07-14): hiding shape X
    // 1) is a no-op if an ancestor of X is already hidden — that ancestor
    //    already covers X, adding X too would create a redundant duplicate
    //    entry (surfaces as the same id twice in UmlHiddenItemsDlg's tree).
    // 2) strips any already-hidden DESCENDANT of X from the list — e.g. hide
    //    class C, then 3 minutes later hide its parent package P: P now covers
    //    C, so C's own separate entry is redundant AND would let a user
    //    "Show" just C while P stays hidden, leaving a class with no visible
    //    parent package on canvas. Un-hiding P later should bring C back with
    //    it, not leave it separately toggleable.
    //
    // Undoable (chat 2026-07-14): pushes a 'hiddenItems' snapshot of the
    // WHOLE previous array (not just this one id) onto the undo stack before
    // any actual change — a single id-level reverse wouldn't correctly
    // restore case 2 above (the removed descendant entries need to come back
    // exactly as they were, not be re-derived). Skipped entirely when nothing
    // actually changes (e.g. no-op hides, see hasHiddenAncestor above), same
    // as removeShape only snapshotting on a real delete.
    setItemHidden(itemId, hidden) {
        const current = this.viewConfig.userHiddenItems || [];
        let next;
        if (hidden) {
            const shape = this.getShape(itemId);
            if (shape && this.hasHiddenAncestor(shape, current)) {
                return; // an ancestor already covers this shape, nothing to add
            }
            const descendantIds = shape
                ? collectShapeIdsDeep(shape).filter(function excludeSelf(id) {
                      return id !== itemId;
                  })
                : [];
            const withoutDescendants = current.filter(function dropDescendant(id) {
                return !descendantIds.includes(id);
            });
            next = withoutDescendants.includes(itemId) ? withoutDescendants : [...withoutDescendants, itemId];
        } else {
            next = current.filter(function filterOutId(id) {
                return id !== itemId;
            });
        }

        const changed =
            next.length !== current.length ||
            next.some(function isNew(id) { return !current.includes(id); }) ||
            current.some(function isGone(id) { return !next.includes(id); });
        if (!changed) return;

        umlUndoEngine.addAction('hiddenItems', { previous: [...current] });
        this.viewConfig = { ...this.viewConfig, userHiddenItems: next };
        this.notify();
    }

    // Walks shape.parentId up the chain, checking whether any ancestor's id is
    // already in hiddenIds — used by setItemHidden to avoid hiding something
    // whose ancestor already covers it (see setItemHidden doc above).
    hasHiddenAncestor(shape, hiddenIds) {
        let parentId = shape.parentId;
        while (parentId) {
            if (hiddenIds.includes(parentId)) return true;
            const parent = this.getShape(parentId);
            parentId = parent ? parent.parentId : null;
        }
        return false;
    }

    // Ctrl+Z target for setItemHidden/clearHiddenItems (see UmlUndoRestorer.
    // _restoreHiddenItems) — puts viewConfig.userHiddenItems back to an EXACT
    // prior snapshot. Bypasses setItemHidden's ancestor/descendant logic
    // entirely on purpose: undo must restore the array as it literally was,
    // not re-derive it through the same rules that produced the new state.
    restoreHiddenItems(previousItems) {
        this.viewConfig = { ...this.viewConfig, userHiddenItems: [...previousItems] };
        this.notify();
    }

    // Clears the entire hidden-items list in one shot (Unhide All button, see
    // UmlHiddenItemsDlg) — single notify() instead of N calls to setItemHidden.
    // Also undoable, same 'hiddenItems' snapshot mechanism as setItemHidden.
    clearHiddenItems() {
        const current = this.viewConfig.userHiddenItems || [];
        if (current.length === 0) return;
        umlUndoEngine.addAction('hiddenItems', { previous: [...current] });
        this.viewConfig = { ...this.viewConfig, userHiddenItems: [] };
        this.notify();
    }

    /**
     * Strips the given ids from viewConfig.userHiddenItems in one shot — called
     * by removeShape below whenever a shape (+ its cascaded connectors) is
     * actually deleted, so userHiddenItems never accumulates dead ids for
     * items that no longer exist ("digitale lijken"). No-op (no notify) if
     * none of the ids were actually in the list.
     * @param {string[]} ids
     */
    removeFromHiddenItems(ids) {
        const current = this.viewConfig.userHiddenItems || [];
        if (current.length === 0) return;
        const idSet = new Set(ids);
        const next = current.filter(function keepId(id) {
            return !idSet.has(id);
        });
        if (next.length === current.length) return;
        this.viewConfig = { ...this.viewConfig, userHiddenItems: next };
        this.notify();
    }

    addShape(shape) {
        shape.bringToFront(this.nextZIndex);
        this.nextZIndex += 1;
        this.shapes.push(shape);
        this.shapes = packageContainmentHandler.tryAdopt(this.shapes, shape); // adopt if dropped/imported inside an existing package's bounds
        this.notify();
    }

    /**
     * Re-adds a previously deleted shape EXACTLY as it was — unlike addShape,
     * this does NOT call bringToFront and does NOT run packageContainmentHandler.tryAdopt.
     *
     * WHY NOT addShape: addShape always bumps the shape to the top of the
     * z-order (bringToFront) and re-runs containment detection, both correct
     * for a NEW shape with no known position/parent. A restored shape is the
     * opposite case — its zIndex and parentId are already correct, captured
     * in the undo snapshot at delete time. Running tryAdopt again could even
     * re-parent it into a DIFFERENT package than before, if its bounds now
     * happen to overlap something they didn't overlap at delete time.
     * @param {object} shape - reconstructed shape (see UmlShapeSerializer.fromPlainShape)
     */
    restoreShape(shape) {
        if (shape.parentId) {
            const parent = this.getShape(shape.parentId);
            if (parent instanceof UmlCompositeShape) {
                // Direct push, not parent.addChild(shape): addChild reassigns
                // parentId/zIndex as if this were a brand-new child — a restore
                // must keep the EXACT values captured in the snapshot instead.
                parent.children.push(shape);
            }
        } else {
            this.shapes.push(shape);
        }
        this.nextZIndex = Math.max(this.nextZIndex, (shape.zIndex || 0) + 1); // stay above the restored zIndex for future new shapes
        this.notify();
    }

    /**
     * Deletes a shape (and, for packages, everything nested inside it).
     *
     * Snapshots the shape + its related connectors onto the undo stack
     * BEFORE mutating anything, then cleans up connectors using the full
     * recursive id list (shape + every nested child) — not just shapeId —
     * so a connector on a class nested inside a deleted package can no
     * longer be left dangling. See UmlUndoEngine.createSnapshot and
     * UmlConnectorEngine.removeConnectorsForShapeIds for the full reasoning.
     *
     * Also strips every deleted id (shapes + their cascaded connectors) from
     * viewConfig.userHiddenItems — otherwise a hidden item deleted this way
     * leaves a dead id behind forever (see removeFromHiddenItems). NOTE: undo
     * (Ctrl+Z) restores the shape/connector objects themselves but does NOT
     * restore their prior hidden-state — a shape that was hidden before
     * deletion comes back visible after undo. Acceptable v1 simplification,
     * not revisited here.
     * @param {string} shapeId
     */
    removeShape(shapeId) {
        const shape = this.getShape(shapeId);
        if (!shape) {
            return;
        }
        umlUndoEngine.addAction('delete', umlUndoEngine.createSnapshot(shape, umlConnectorEngine.connectors));
        const shapeIds = collectShapeIdsDeep(shape);
        const removedConnectorIds = umlConnectorEngine.removeConnectorsForShapeIds(shapeIds);
        this.removeFromHiddenItems([...shapeIds, ...removedConnectorIds]);

        // Nested shapes (parentId set) live in their parent's children[], NOT
        // in this.shapes — filtering this.shapes alone was always a no-op for
        // them. Top-level shapes (no parentId) are the only ones actually IN
        // this.shapes, so that's the only case the array filter covers.
        if (shape.parentId) {
            const parent = this.getShape(shape.parentId);
            if (parent instanceof UmlCompositeShape) {
                parent.removeChild(shapeId);
            }
        } else {
            this.shapes = this.shapes.filter(function filterOutShape(s) {
                return s.id !== shapeId;
            });
        }
        this.notify();
    }

    getShape(shapeId) {
        return findById(this.shapes, shapeId) || findNestedShape(this.shapes, shapeId);
    }

    bringShapeToFront(shapeId) {
        const shape = this.getShape(shapeId);
        if (!shape) {
            return;
        }
        shape.bringToFront(this.nextZIndex);
        this.nextZIndex += 1;
        this.notify();
    }

    /**
     * Only mutation entry point for UmlShape positions. Checks containment both ways:
     * escape (leaving current parent package) and re-entry (entering a new one).
     * @param {string} shapeId
     * @param {number} x
     * @param {number} y
     * @param {{shiftKey?: boolean}} [options] - shiftKey: held during an activation-bar
     *   drag, this triggers the nested group-move (see cascadeNestedSeqDiaShift);
     *   a plain message drag NEVER cascades, shiftKey or not — a standalone arrow
     *   move must stay standalone. NOT ctrlKey: Ctrl+mousedown on a shape already
     *   means "draw a connector" elsewhere in this app (see
     *   UmlShapeWidgetBasehandleDragMouseDownHelper.jsx) and occasionally fired that
     *   instead of a drag (bug found 2026-07-19) — Shift doesn't collide with that.
     */
    updateShapePosition(shapeId, x, y, options = {}) {
        const shape = this.getShape(shapeId);
        if (!shape) {
            return;
        }
        // Bottom sequence-diagram duplicate participant only moves VERTICALLY —
        // its x is dictated by the top participant's own cascade (see
        // CsSeqDiaRelationHandler), never by direct user drag. Same for a
        // message arrow or activation bar: draggable up/down to reposition it
        // in the sequence, but x/width stays locked to whatever its
        // participant(s) dictate — never pulled loose sideways. A message's y
        // is additionally clamped (activation bounds + call/return ordering).
        const isBottomSeqTwin = shape instanceof UmlSeqParticipantShape && Boolean(shape.linkedParticipantId);
        const isSeqMessage = shape instanceof UmlSeqMessageShape;
        const isSeqActivation = shape instanceof UmlSeqActivationShape;
        const effectiveX = (isBottomSeqTwin || isSeqMessage || isSeqActivation) ? shape.x : x;
        const effectiveY = isSeqMessage ? clampSeqMessageY(this.shapes, shape, y) : y;

        const dx = effectiveX - shape.x;
        const dy = effectiveY - shape.y;
        const oldBounds = { x: shape.x, y: shape.y, w: shape.w, h: shape.h };
        shape.moveTo(effectiveX, effectiveY);
        cascadeSeqDiaMove(this.shapes, shape, dx, dy);
        // Nested group-move: ONLY an activation bar, and ONLY with Shift held —
        // an arrow must stay independently draggable by default (agreed
        // 2026-07-19), so a plain message drag never brings its nested content along.
        if (isSeqActivation && options.shiftKey) {
            cascadeNestedSeqDiaShift(this.shapes, shape, dy);
        }

        if (shape instanceof UmlCompositeShape) {
            translateChildren(shape, dx, dy, this.shapes);
            // Waypoints/labels are absolute canvas coords (unlike manual connector
            // anchors, which are shape-relative and so already track correctly).
            // Only for connectors that actually touch this package (one endpoint is
            // the package itself or one of its descendants) — NOT pure geometric
            // containment of the point against oldBounds, which used to drag along
            // any unrelated connector's waypoint that happened to visually sit inside
            // this package's area (a package drag could "steal" a nearby waypoint
            // belonging to a connector with nothing to do with it).
            const descendantIds = new Set(collectShapeIdsDeep(shape));
            umlConnectorEngine.connectors.forEach(function translateContainedPoints(connector) {
                const touchesThisPackage = descendantIds.has(connector.fromShapeId) || descendantIds.has(connector.toShapeId);
                if (!touchesThisPackage) return;
                connector.points.forEach(function movePointIfInside(point) {
                    if (isPointInBounds(point.x, point.y, oldBounds)) {
                        point.moveTo(point.x + dx, point.y + dy);
                    }
                });
                if (connector.label && isPointInBounds(connector.label.x, connector.label.y, oldBounds)) {
                    connector.label.moveTo(connector.label.x + dx, connector.label.y + dy);
                }
            });
        }

        if (shape.parentId) {
            const parent = this.getShape(shape.parentId);
            if (parent instanceof UmlCompositeShape && !parent.containsShape(shape)) {
                shape.parentId = null; // escaped
                parent.removeChild(shapeId);
                this.shapes.push(shape); // otherwise unreachable: not in children[], not in shapes[]
                shape.bringToFront(this.nextZIndex);
                this.nextZIndex += 1;
            }
        } else {
            this.shapes = packageContainmentHandler.tryAdopt(this.shapes, shape);
        }

        this.notify();
    }

    /**
     * Only mutation entry point for UmlShape dimensions. Rechecks package containment
     * when resizing a package, since shrink/grow can escape or adopt children.
     */
    resizeShape(shapeId, w, h) {
        const shape = this.getShape(shapeId);
        if (!shape) {
            return;
        }
        shape.resizeTo(w, h);
        if (shape instanceof UmlCompositeShape) {
            const result = packageContainmentHandler.recheckContainment(this.shapes, shape, this.nextZIndex);
            this.shapes = result.shapes;
            this.nextZIndex = result.nextZIndex;
        }
        this.notify();
    }

    // Mutation entry point for UmlFreePoint instances (UmlCaption, UmlConnectorPoint). No containment check.
    updatePointPosition(point, x, y) {
        point.moveTo(x, y);
        this.notify();
    }

    // Moves a UmlSeqMessageShape's own label (a UmlCaption, x/y are an offset
    // relative to the message shape's own x/y) — independently draggable, same
    // pattern as a UmlConnector's label, see UmlSeqMessageWidget. Operates by
    // shapeId (not the label object directly) for consistency with every other
    // mutation entry point here.
    updateSeqMessageLabelOffset(shapeId, x, y) {
        const shape = this.getShape(shapeId);
        if (!shape || !shape.label) {
            return;
        }
        shape.label.moveTo(x, y);
        this.notify();
    }

    // Mutation entry point for a shape's textual name/caption (Package.name or UmlClassShape.caption.text).
    renameShape(shapeId, value) {
        const shape = this.getShape(shapeId);
        if (!shape) {
            return;
        }
        if (shape.caption) {
            shape.caption.text = value;
        } else if ('text' in shape) {
            shape.text = value;
        } else if ('name' in shape) {
            shape.name = value;
        }
        this.notify();
    }

    // Toggle for a shape's persisted `collapsed` field (see ShapeGeoBuilder.effectiveHeight
    // and UmlShape.js) — generic, works for any shape kind, not just UmlClassShape.
    setShapeCollapsed(shapeId, collapsed) {
        const shape = this.getShape(shapeId);
        if (!shape || !('collapsed' in shape)) {
            return;
        }
        shape.setCollapsed(collapsed);
        this.notify();
    }

    // Icon-picker selection for UmlIconShape (see UmlIconWidget.jsx) — same shape as
    // setShapeCollapsed above.
    setShapeIconKind(shapeId, iconKind) {
        const shape = this.getShape(shapeId);
        if (!shape || !('iconKind' in shape)) {
            return;
        }
        shape.setIconKind(iconKind);
        this.notify();
    }
}

const umlCanvasEngine = new UmlCanvasEngine();
export default umlCanvasEngine;
